/**
 * DSD-DSD 대사 결과 뷰
 *
 * 백엔드 JSON element(DiffDsdTable / DiffParagraphGroup)를
 * toggle-ui-kit의 DsdHeaderCell, DsdCell, DsdTextBlock으로 렌더링.
 *
 * 섹션 단위로 하나의 카드에 좌(src)·우(trg) 패널을 배치.
 */

import { compareDsdDsd } from "@/apis/dsd-dsd-compare/route";
import type {
  DiffCell,
  DiffDsdTable,
  DiffElement,
  DiffParagraph,
  DiffParagraphGroup,
  DiffRow,
  DiffSpan,
  DsdDsdCompareItem,
  DsdDsdCompareResponse,
} from "@/apis/dsd-dsd-compare/types";
import {
  CompareSidebar,
  type CompareSidebarCategory,
  type CompareSidebarSection,
} from "@/components/compare/compare-sidebar";
import { useLogDsdDsdCompareDownload } from "@/hooks/mutations";
import {
  MULTI_SELECT_CELL_BORDER_CSS,
  selectableCellAttrs,
  useTableCellSelection,
} from "@/hooks/use-table-cell-selection";
import { cn } from "@/lib/utils";
import { SidebarInset, SidebarProvider } from "@/styles/ui/sidebar";
import {
  DsdCell,
  DsdHeaderCell,
  DsdTextBlock,
} from "@/toggle-ui-kit/dsd-table";
import { TableScrollArea } from "@/toggle-ui-kit/table-scroll-area";
import { DsdCellText } from "@/utils/dsd-cell-text";
import {
  dsdTableLayoutOptionsFrom,
  useDsdTableLayout,
} from "@/toggle-ui-kit/use-dsd-table-layout";
import { useMutation } from "@tanstack/react-query";
import { CirclePlus, CircleX, Loader2 } from "lucide-react";
import {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { flushSync } from "react-dom";
import { useSearchParams } from "react-router-dom";
import {
  buildCellIdToChangeIndex,
  buildChangeList,
  buildParagraphIdToChangeIndex,
  buildSpanIdToChangeIndex,
  ChangeNavigatorPanel,
  groupChangedSpans,
  isChangeParagraph,
} from "./change-navigator";
import {
  CompareHeader,
  isChangesActive,
  isSumDiffsActive,
  type DsdDsdViewMode,
} from "./compare-header";
import { downloadCompareHtml } from "./download-compare-html";
import {
  resolveCellVisuals,
  resolvePlainBorder,
  resolveSelectionInfo,
  toChangeStatus,
} from "./mode-visual-config";

// ============================================
// 셀 선택 유틸
// ============================================

interface CellSelectionProps {
  selectedCellId: string | null;
  sumPairCellId: string | null;
  focusedCellIds: ReadonlySet<string>;
  highlightedCellIds: ReadonlySet<string>;
  plainSelectedCellIds: ReadonlySet<string>;
  activeParagraphId: string | null;
  activeSpanChangeId: string | null;
  onCellSelect: (cellId: string) => void;
  /** 드래그 선택/복사용 고유 DOM id. 미지정 시 셀은 선택 대상이 아니다. */
  getCellDomId?: (cell: DiffCell) => string | undefined;
  onParagraphSelect: (paragraphId: string) => void;
  onSpanSelect: (spanId: string) => void;
}

const EMPTY_SET: ReadonlySet<string> = new Set();

const buildCellMap = (result: DsdDsdCompareResponse): Map<string, DiffCell> => {
  const map = new Map<string, DiffCell>();
  for (const item of result.items) {
    for (const element of [item.srcElement, item.trgElement, item.element]) {
      if (element && "rows" in element) {
        for (const row of element.rows) {
          for (const cell of row.cells) {
            map.set(cell.cellId, cell);
          }
        }
      }
    }
  }
  return map;
};

// ============================================
// 셀 선택 시 스크롤 (세로 + 중첩 가로 overflow)
// ============================================

const HORIZONTAL_SCROLL_MARGIN_PX = 12;

/** data-cell-id 속성 셀렉터용 이스케이프 */
const cellIdAttrSelector = (cellId: string) =>
  `[data-cell-id="${CSS.escape(cellId)}"]`;

/**
 * 대사 뷰 셀의 고유 DOM id(드래그 선택 키). src/trg 패널의 cellId 는 서로 다르므로
 * (cellPairMap 이 주소로 잇는 게 그 증거) cellId 기반으로 충돌 없이 유일하다.
 * prefix 는 같은 화면의 다른 id 네임스페이스(section-* 등)와의 충돌 방지용.
 */
const compareCellDomId = (cellId: string): string =>
  `dsd-compare-cell-${cellId}`;

/**
 * 문단/스팬 변경 id(`para_${itemIndex}_…`, `span_${itemIndex}_…`)에서 소속 아이템
 * 인덱스를 읽는다. 선택 상태를 해당 아이템 카드에만 전달하기 위한 역방향 조회.
 */
const itemIndexFromChangeId = (id: string | null): number | null => {
  if (!id) return null;
  const match = id.match(/^(?:para|span)_(\d+)_/);
  return match ? Number(match[1]) : null;
};

function getDomDepth(node: HTMLElement, root: HTMLElement): number {
  let depth = 0;
  let n: HTMLElement | null = node;
  while (n) {
    if (n === root) return depth;
    depth++;
    n = n.parentElement;
  }
  return depth;
}

function isHorizontallyScrollable(el: HTMLElement): boolean {
  const ox = getComputedStyle(el).overflowX;
  if (ox !== "auto" && ox !== "scroll") return false;
  return el.scrollWidth > el.clientWidth + 1;
}

/** root 아래, el의 가로 스크롤 가능한 조상들 (가까운 쪽이 앞) */
function collectHorizontalScrollersForElement(
  root: HTMLElement,
  el: HTMLElement,
): HTMLElement[] {
  const list: HTMLElement[] = [];
  let p: HTMLElement | null = el.parentElement;
  while (p && p !== root) {
    if (isHorizontallyScrollable(p)) list.push(p);
    p = p.parentElement;
  }
  return list;
}

/** 스크롤러 안에 포함된 타깃들의 가로 범위가 한 번에 보이도록 scrollLeft 조정 */
function alignHorizontalScrollerToElements(
  scroller: HTMLElement,
  elements: HTMLElement[],
  margin: number,
  behavior: ScrollBehavior,
) {
  const visible = elements.filter((e) => scroller.contains(e));
  if (visible.length === 0) return;
  const sRect = scroller.getBoundingClientRect();
  let minL = Infinity;
  let maxR = -Infinity;
  for (const t of visible) {
    const r = t.getBoundingClientRect();
    minL = Math.min(minL, r.left);
    maxR = Math.max(maxR, r.right);
  }
  if (minL === Infinity) return;
  let delta = 0;
  if (minL < sRect.left + margin) {
    delta = minL - sRect.left - margin;
  } else if (maxR > sRect.right - margin) {
    delta = maxR - sRect.right + margin;
  }
  if (delta !== 0) {
    scroller.scrollBy({ left: delta, behavior });
  }
}

/**
 * 대사 뷰 스크롤 루트 기준으로 셀(들)이 세로·가로 모두 보이도록 스크롤.
 * TableHorizontalNavigator 내부 등 중첩 overflow-x는 scrollIntoView만으로 부족할 수 있어 조상별로 보정.
 */
function scrollCompareCellTargetsIntoView(
  root: HTMLElement,
  cellIds: readonly string[],
  behavior: ScrollBehavior = "smooth",
) {
  const uniqueIds = [...new Set(cellIds.filter(Boolean))];
  const elements = uniqueIds
    .map((id) => root.querySelector(cellIdAttrSelector(id)))
    .filter((n): n is HTMLElement => n instanceof HTMLElement);
  if (elements.length === 0) return;

  elements[0].scrollIntoView({
    behavior,
    block: "center",
    inline: "nearest",
  });

  requestAnimationFrame(() => {
    const scrollers = new Set<HTMLElement>();
    for (const el of elements) {
      for (const sc of collectHorizontalScrollersForElement(root, el)) {
        scrollers.add(sc);
      }
    }
    const ordered = [...scrollers].sort(
      (a, b) => getDomDepth(b, root) - getDomDepth(a, root),
    );
    for (const sc of ordered) {
      alignHorizontalScrollerToElements(
        sc,
        elements,
        HORIZONTAL_SCROLL_MARGIN_PX,
        "auto",
      );
    }
  });
}

// ============================================
// DiffSpan 렌더링
// ============================================

const spanStyles: Record<string, string> = {
  equal: "",
  insert: "bg-lime-200",
  delete: "bg-red-200 line-through",
};

const DiffSpanView = ({
  span,
  modified,
}: {
  span: DiffSpan;
  modified?: boolean;
}) => {
  if (span.kind === "equal") return <>{span.text}</>;
  const style = modified ? "bg-indigo-200" : spanStyles[span.kind];
  if (!style) return <>{span.text}</>;
  return <span className={style}>{span.text}</span>;
};

/** spans를 공백으로 이어 렌더 — 백엔드가 단어 단위로 쪼개 보내므로 구분자가 필요 */
const DiffSpansText = ({ spans }: { spans: DiffSpan[] }) => (
  <>
    {spans.map((span, i) => (
      <Fragment key={i}>
        {i > 0 && " "}
        <DiffSpanView span={span} modified />
      </Fragment>
    ))}
  </>
);

// ============================================
// DiffParagraph 렌더링 → DsdTextBlock
// ============================================

const paragraphBgMap: Record<string, string> = {
  deleted: "bg-red-200",
  inserted: "bg-lime-200",
};

const DiffParagraphView = ({ paragraph }: { paragraph: DiffParagraph }) => {
  const bg = paragraphBgMap[paragraph.status] ?? "";

  return (
    <p className={bg}>
      {paragraph.spans.length > 0
        ? paragraph.spans.map((span, i) => (
            <DiffSpanView
              key={i}
              span={span}
              modified={paragraph.status === "modified"}
            />
          ))
        : paragraph.text}
    </p>
  );
};

/** 삭제/추가된 개별 문단 — 클릭/포커스/스크롤 타겟 */
const paragraphRingColor: Record<string, string> = {
  deleted: "ring-2 ring-red-500",
  inserted: "ring-2 ring-lime-500",
};

const DiffParagraphChangeView = ({
  paragraph,
  paragraphId,
  isActive,
  onSelect,
}: {
  paragraph: DiffParagraph;
  paragraphId: string;
  isActive: boolean;
  onSelect: (id: string) => void;
}) => (
  <div
    data-change-id={paragraphId}
    className={cn(
      "cursor-pointer rounded-sm",
      isActive &&
        (paragraphRingColor[paragraph.status] ?? "ring-2 ring-indigo-500"),
    )}
    onClick={(e) => {
      e.stopPropagation();
      onSelect(paragraphId);
    }}
  >
    <DiffParagraphView paragraph={paragraph} />
  </div>
);

/** modified 문단 — span 변경 그룹 단위로 클릭/포커스 */
const DiffModifiedParagraphView = ({
  paragraph,
  itemIndex,
  paraIndex,
  activeSpanChangeId,
  onSpanSelect,
}: {
  paragraph: DiffParagraph;
  itemIndex: number;
  paraIndex: number;
  activeSpanChangeId: string | null;
  onSpanSelect: (id: string) => void;
}) => {
  const bg = paragraphBgMap[paragraph.status] ?? "";
  const spanGroups = groupChangedSpans(paragraph.spans);

  // span을 순서대로 렌더, 변경 그룹은 클릭 가능한 wrapper로 감싸기
  const elements: React.ReactNode[] = [];
  let spanIdx = 0;

  for (const [gi, [start, end]] of spanGroups.entries()) {
    // 변경 그룹 앞의 equal spans
    for (; spanIdx < start; spanIdx++) {
      elements.push(
        <DiffSpanView key={spanIdx} span={paragraph.spans[spanIdx]} modified />,
      );
    }

    // 변경 그룹
    const changeId = `span_${itemIndex}_${paraIndex}_${gi}`;
    const isActive = activeSpanChangeId === changeId;
    elements.push(
      <span
        key={`g${gi}`}
        data-change-id={changeId}
        className={cn(
          "cursor-pointer rounded-sm",
          isActive && "ring-2 ring-indigo-500",
        )}
        onClick={(e) => {
          e.stopPropagation();
          onSpanSelect(changeId);
        }}
      >
        {paragraph.spans.slice(start, end).map((span, si) => (
          <DiffSpanView key={start + si} span={span} modified />
        ))}
      </span>,
    );
    spanIdx = end;
  }

  // 나머지 equal spans
  for (; spanIdx < paragraph.spans.length; spanIdx++) {
    elements.push(
      <DiffSpanView key={spanIdx} span={paragraph.spans[spanIdx]} modified />,
    );
  }

  return <p className={bg}>{elements}</p>;
};

export const DiffParagraphGroupView = ({
  group,
  itemIndex,
  activeParagraphId,
  activeSpanChangeId,
  onParagraphSelect,
  onSpanSelect,
}: {
  group: DiffParagraphGroup;
  itemIndex: number;
  activeParagraphId: string | null;
  activeSpanChangeId: string | null;
  onParagraphSelect: (paragraphId: string) => void;
  onSpanSelect: (spanId: string) => void;
}) => {
  const groupStatus = toChangeStatus(
    group.paragraphs.find((p) => p.status !== "equal")?.status ?? "equal",
  );

  return (
    <DsdTextBlock changeStatus={groupStatus}>
      {group.paragraphs.map((p, i) => {
        if (!isChangeParagraph(p)) {
          return <DiffParagraphView key={i} paragraph={p} />;
        }

        // modified → span-level navigation
        if (p.status === "modified") {
          return (
            <DiffModifiedParagraphView
              key={i}
              paragraph={p}
              itemIndex={itemIndex}
              paraIndex={i}
              activeSpanChangeId={activeSpanChangeId}
              onSpanSelect={onSpanSelect}
            />
          );
        }

        // deleted/inserted → paragraph-level
        return (
          <DiffParagraphChangeView
            key={i}
            paragraph={p}
            paragraphId={`para_${itemIndex}_${i}`}
            isActive={activeParagraphId === `para_${itemIndex}_${i}`}
            onSelect={onParagraphSelect}
          />
        );
      })}
    </DsdTextBlock>
  );
};

// ============================================
// Matched Paragraph 쌍 — 2-column grid 정렬
// ============================================

const renderParagraphItem = (
  p: DiffParagraph,
  itemIndex: number,
  paraIndex: number,
  activeParagraphId: string | null,
  activeSpanChangeId: string | null,
  onParagraphSelect: (paragraphId: string) => void,
  onSpanSelect: (spanId: string) => void,
): React.ReactNode => {
  if (!p.text && !isChangeParagraph(p)) return null;

  if (!isChangeParagraph(p)) {
    return <DiffParagraphView paragraph={p} />;
  }
  if (p.status === "modified") {
    return (
      <DiffModifiedParagraphView
        paragraph={p}
        itemIndex={itemIndex}
        paraIndex={paraIndex}
        activeSpanChangeId={activeSpanChangeId}
        onSpanSelect={onSpanSelect}
      />
    );
  }
  return (
    <DiffParagraphChangeView
      paragraph={p}
      paragraphId={`para_${itemIndex}_${paraIndex}`}
      isActive={activeParagraphId === `para_${itemIndex}_${paraIndex}`}
      onSelect={onParagraphSelect}
    />
  );
};

const isParagraphElement = (el: DiffElement): el is DiffParagraphGroup =>
  "paragraphs" in el;

/** matched paragraph 쌍을 2-column grid로 렌더링. 같은 행의 셀 높이가 자동 정렬됨. */
const DiffParagraphPairGridView = ({
  srcGroup,
  trgGroup,
  itemIndex,
  activeParagraphId,
  activeSpanChangeId,
  onParagraphSelect,
  onSpanSelect,
}: {
  srcGroup: DiffParagraphGroup;
  trgGroup: DiffParagraphGroup;
  itemIndex: number;
  activeParagraphId: string | null;
  activeSpanChangeId: string | null;
  onParagraphSelect: (paragraphId: string) => void;
  onSpanSelect: (spanId: string) => void;
}) => {
  const len = Math.max(srcGroup.paragraphs.length, trgGroup.paragraphs.length);

  return (
    <div className="grid grid-cols-2 gap-x-6 gap-y-3 px-2 py-1 label-2-medium text-label-normal">
      {Array.from({ length: len }, (_, i) => (
        <Fragment key={i}>
          <div className="min-w-0">
            {srcGroup.paragraphs[i] &&
              renderParagraphItem(
                srcGroup.paragraphs[i],
                itemIndex,
                i,
                activeParagraphId,
                activeSpanChangeId,
                onParagraphSelect,
                onSpanSelect,
              )}
          </div>
          <div className="min-w-0">
            {trgGroup.paragraphs[i] &&
              renderParagraphItem(
                trgGroup.paragraphs[i],
                itemIndex,
                i,
                activeParagraphId,
                activeSpanChangeId,
                onParagraphSelect,
                onSpanSelect,
              )}
          </div>
        </Fragment>
      ))}
    </div>
  );
};

// ============================================
// DiffCell 렌더링 → DsdHeaderCell / DsdCell
// ============================================

const buildCellStyle = (
  cell: DiffCell,
  /** 컴포넌트가 원본 위에 다른 배경(변경색/하이라이트/합계)을 덮는 경우 원본 배경색 제거 */
  stripBackground = false,
): React.CSSProperties | undefined => {
  const result: Record<string, string> = {};
  if (cell.style) {
    for (const part of cell.style.split(";")) {
      const [key, ...vals] = part.split(":");
      if (!key || vals.length === 0) continue;
      const prop = key.trim().replace(/-([a-z])/g, (_, c) => c.toUpperCase());
      if (stripBackground && prop === "backgroundColor") continue;
      result[prop] = vals.join(":").trim();
    }
  }
  return Object.keys(result).length > 0 ? result : undefined;
};

const DiffCellView = ({
  cell,
  mode,
  cornerClassName,
  selectedCellId,
  sumPairCellId,
  focusedCellIds,
  highlightedCellIds,
  plainSelectedCellIds,
  onCellSelect,
  getCellDomId,
}: {
  cell: DiffCell;
  mode: DsdDsdViewMode;
  cornerClassName?: string;
} & CellSelectionProps) => {
  const modeInput = {
    rawStatus: cell.status,
    sumCaution: cell.sumCaution,
    sumRole: cell.sumRole,
    sumDifference: cell.sumDifference,
  };

  const isSelected = selectedCellId === cell.cellId;
  // 합계/합계불일치 pair → 자기 데이터로 full selection 처리
  const isSumPairSelected = cell.cellId === sumPairCellId;
  const effectiveSelected = isSelected || isSumPairSelected;
  const isPlainSelected = plainSelectedCellIds.has(cell.cellId);

  // ghost 아닌 모든 셀 클릭 허용
  const handleClick = cell.isGhost
    ? undefined
    : (e: React.MouseEvent<HTMLTableCellElement>) => {
        e.stopPropagation();
        onCellSelect(cell.cellId);
      };
  // DSD 원문의 셀 내 강제 개행(`&cr;`)은 cell.value 에 `\n` 으로 살아 온다. 기본
  // white-space 로 렌더하면 그게 공백 한 칸으로 뭉개져, 보고서에서 두 줄인 헤더가
  // 한 줄로 보인다 — 원문 재현 실패이자, 대사에서 "변경인데 똑같아 보이는" 원인이다.
  // 셀에 whitespace-pre-line 을 걸어 개행을 살린다(아래 className).
  //
  // 값 앞뒤에 붙어 오는 `\n` 빈 줄은 없애고, 원문 들여쓰기(앞 공백)는 살린다 — DsdCellText.
  const cellText = DsdCellText.of(cell.value);
  // 단어 diff spans 는 백엔드가 단어로 쪼개며 앞 공백을 잃으므로, 원문 들여쓰기를 앞에 붙인다.
  const cellValue = cell.spans?.length ? (
    <>
      {cellText.indent}
      <DiffSpansText spans={cell.spans} />
    </>
  ) : (
    cellText.display
  );

  // 드래그 선택/복사 대상 id. 헤더(th)와 ghost(비교 정렬용 placeholder)도 포함한다 —
  // ghost 가 빠지면 복사 결과에서 그 자리 빈 칸이 사라져 열이 왼쪽으로 밀린다.
  // 클릭 액션은 기존대로 ghost 제외.
  const selectableCellDomId = getCellDomId?.(cell);
  // 복사값은 앞뒤 공백을 뺀다 — 엑셀에서 숫자로 읽히게. 백엔드 셀 비교도 strip 후 판정.
  const selectableAttrs = selectableCellDomId
    ? selectableCellAttrs({
        uniqueId: selectableCellDomId,
        cellId: cell.cellId,
        displayValue: cellText.clipboard,
      })
    : // getCellDomId 미지정 소비자는 선택/복사 계약 대상이 아니다. cellId 만
      // 남기는 건 하이라이트·스크롤(cellIdAttrSelector)과 다운로드 HTML 의
      // 셀 클릭 핸들러가 이 속성으로 셀을 찾기 때문.
      { "data-cell-id": cell.cellId };

  if (cell.isHeader) {
    // 헤더도 body 셀과 같은 모드 규칙을 따른다 — 합계불일치 모드에선 변경색 억제
    const headerChangeStatus = resolveCellVisuals(mode, modeInput).changeStatus;
    const headerStyle = buildCellStyle(cell, headerChangeStatus !== "default");
    const scope =
      cell.attrs.scope === "row" || cell.attrs.scope === "col"
        ? cell.attrs.scope
        : undefined;
    const selectionInfo = resolveSelectionInfo(mode, modeInput);
    const isFocused = focusedCellIds.has(cell.cellId);
    const showOverlay =
      effectiveSelected || (isFocused && cell.status === "modified");

    return (
      <DsdHeaderCell
        {...selectableAttrs}
        scope={scope}
        changeStatus={headerChangeStatus}
        ghost={cell.isGhost}
        rowSpan={cell.rowspan}
        colSpan={cell.colspan}
        style={headerStyle}
        selectionColor={showOverlay ? selectionInfo?.color : undefined}
        selectionLabel={showOverlay ? selectionInfo?.label : undefined}
        focused={isFocused}
        plainSelectionBorder={
          isPlainSelected && !isSumPairSelected
            ? resolvePlainBorder(selectionInfo?.color)
            : undefined
        }
        onClick={handleClick}
        className={cn(
          "whitespace-pre-line",
          cornerClassName,
          selectionInfo && "cursor-pointer",
        )}
      >
        {cellValue}
      </DsdHeaderCell>
    );
  }

  const visuals = resolveCellVisuals(mode, modeInput);
  const isFocused = focusedCellIds.has(cell.cellId);
  const isHighlighted = highlightedCellIds.has(cell.cellId);

  // nb 표 셀은 word-diff spans가 변경 위치를 표시하므로, 셀 통짜 배경/인디케이터를
  // 끈다 (문단 MODIFIED가 전체 배경 없이 spans만 칠하는 것과 동일). 안 끄면 셀 배경과
  // 변경 단어가 같은 indigo라 서로 묻혀 변경 위치가 안 보인다.
  const hasWordSpans = !!cell.spans?.length;

  // ── 선택/하이라이트 상태에 따른 시각 override ──
  const showOverlay =
    effectiveSelected || (isFocused && cell.status === "modified");
  const finalChangeStatus = hasWordSpans ? "default" : visuals.changeStatus;
  const finalCellIndicator =
    effectiveSelected || isHighlighted || hasWordSpans
      ? false
      : visuals.cellIndicator;
  const finalChipVariant = isHighlighted ? "dark" : visuals.sumDiffChipVariant;
  const finalPlainBorder =
    isPlainSelected && !isSumPairSelected
      ? resolvePlainBorder(visuals.selectionColor)
      : undefined;

  // DSD 원본 inline backgroundColor 제거: 컴포넌트가 다른 bg를 적용하는 경우만
  // (selection/focus는 overlay로 처리하므로 원본 bg 유지)
  const cellStyle = buildCellStyle(
    cell,
    visuals.changeStatus !== "default" ||
      isHighlighted ||
      cell.sumCaution ||
      visuals.sumStatus === "sum-match",
  );

  return (
    <DsdCell
      {...selectableAttrs}
      changeStatus={finalChangeStatus}
      cellIndicator={finalCellIndicator}
      indicatorPriority={visuals.indicatorPriority}
      ghost={cell.isGhost}
      sumStatus={visuals.sumStatus}
      sumDiffLabel={visuals.sumDiffLabel}
      showSumChips={visuals.showSumChips}
      sumDiffChipVariant={finalChipVariant}
      rowSpan={cell.rowspan}
      colSpan={cell.colspan}
      style={cellStyle}
      selected={effectiveSelected}
      selectionColor={showOverlay ? visuals.selectionColor : undefined}
      selectionLabel={showOverlay ? visuals.selectionLabel : undefined}
      highlighted={isHighlighted}
      focused={isFocused}
      plainSelectionBorder={finalPlainBorder}
      onClick={handleClick}
      className={cn(
        "whitespace-pre-line",
        cornerClassName,
        visuals.selectionColor && "cursor-pointer",
      )}
    >
      {cellValue}
    </DsdCell>
  );
};

// ============================================
// DiffDsdTable 렌더링
// ============================================

const DiffRowView = ({
  row,
  mode,
  rowClassName: rowCls,
  cellClassName: cellCls,
  ...selectionProps
}: {
  row: DiffRow;
  mode: DsdDsdViewMode;
  rowClassName?: string;
  cellClassName: (cellId: string) => string | undefined;
} & CellSelectionProps) => (
  <tr className={rowCls}>
    {row.cells.map((cell) => (
      <DiffCellView
        key={cell.cellId}
        cell={cell}
        mode={mode}
        cornerClassName={cellCls(cell.cellId)}
        {...selectionProps}
      />
    ))}
  </tr>
);

export const DiffTableView = ({
  table,
  mode,
  ...selectionProps
}: {
  table: DiffDsdTable;
  mode: DsdDsdViewMode;
} & CellSelectionProps) => {
  const layout = useDsdTableLayout(
    table.rows,
    dsdTableLayoutOptionsFrom(table.attrs),
  );
  const tableRef = useRef<HTMLTableElement>(null);
  // 직전 run 에서 min-width 를 설정한 td 들. 다음 run 시작 시 전부 되돌려, 모드 전환
  // 등으로 더 이상 sum-match 가 아니게 된 셀의 stale min-width 가 남지 않게 한다.
  const adjustedTdsRef = useRef<HTMLElement[]>([]);

  // 합계 칩 셀 우측 숫자 잘림 보정.
  // 컬럼 폭이 DSD 원본 명시 width(attrs.width+16)에 고정되는데, 인라인 "합계"
  // 칩이 숫자 공간을 먹어 숫자(flex-1 min-w-0)가 짓눌려 잘린다. 명시 width 로는
  // 컬럼이 콘텐츠만큼 자라지 않으므로(실측 확인), 렌더 후 실제 잘림(clip)을 재서
  // "부족한 셀만 부족한 만큼" min-width 로 보정한다. (예측 공식은 안 잘리는 셀까지
  // 건드려 과확대됨 → 실측이 정확.) base 상태에서 일괄 측정 후 적용해 같은 컬럼 내
  // 셀 간 결합에 순서 비의존.
  useLayoutEffect(() => {
    const root = tableRef.current;
    if (!root) return;
    // 1) 직전 run 에서 보정한 셀 전부 해제. "이번에 찾은 셀만 해제"하면, 모드 전환으로
    //    칩을 잃어 새 쿼리에 안 잡히는 셀의 min-width 가 stale 로 남는다(같은 td DOM 재사용).
    adjustedTdsRef.current.forEach((td) =>
      td.style.removeProperty("min-width"),
    );
    adjustedTdsRef.current = [];

    // 2) 현재 sum-match 칩 셀 수집. 숫자 span 은 의미 마커(data-sum-number)로 찾는다 —
    //    .min-w-0 같은 레이아웃 유틸 클래스에 의존하면 디자인 변경 시 조용히 깨지므로.
    const seen = new Set<HTMLElement>();
    const cells: { td: HTMLElement; num: HTMLElement }[] = [];
    root
      .querySelectorAll<HTMLElement>('[data-chip-type="sum-match"]')
      .forEach((chip) => {
        const td = chip.closest("td");
        if (!(td instanceof HTMLElement) || seen.has(td)) return;
        const num = td.querySelector<HTMLElement>("[data-sum-number]");
        if (!num) return;
        seen.add(td);
        cells.push({ td, num });
      });
    if (cells.length === 0) return;

    // 3) base 상태로 reflow 후 일괄 측정 → 적용. 적용한 td 는 다음 run reset 용으로 기록.
    void root.offsetWidth;
    cells
      .map(({ td, num }) => ({
        td,
        baseW: td.offsetWidth,
        clip: num.scrollWidth - num.clientWidth,
      }))
      .forEach(({ td, baseW, clip }) => {
        if (clip > 1) {
          td.style.minWidth = `${baseW + clip + 2}px`;
          adjustedTdsRef.current.push(td);
        }
      });
  }, [table, mode]);

  return (
    /* 첫 행 셀 선택 시 상단 indicator 탭이 overflow-x-auto에 clip되지 않도록 여백 확보 */
    <div className="pt-6">
      <TableScrollArea>
        <table ref={tableRef} className={layout.tableClassName}>
          {table.colGroups.map((colGroup, colGroupIndex) => (
            <colgroup key={colGroupIndex}>
              {colGroup.cols.map((col, colIndex) => (
                <col
                  key={colIndex}
                  style={
                    typeof col.width === "number"
                      ? { width: col.width }
                      : undefined
                  }
                />
              ))}
            </colgroup>
          ))}
          <tbody>
            {table.rows.map((row, i) => (
              <DiffRowView
                key={i}
                row={row}
                mode={mode}
                rowClassName={layout.rowClassName(i)}
                cellClassName={layout.cellClassName}
                {...selectionProps}
              />
            ))}
          </tbody>
        </table>
      </TableScrollArea>
    </div>
  );
};

// ============================================
// DiffElement 디스패치 (table or paragraph)
// ============================================

const isTable = (el: DiffElement): el is DiffDsdTable => "rows" in el;

export const DiffElementView = ({
  element,
  mode,
  itemIndex,
  ...selectionProps
}: {
  element: DiffElement;
  mode: DsdDsdViewMode;
  itemIndex: number;
} & CellSelectionProps) => {
  if (isTable(element)) {
    return <DiffTableView table={element} mode={mode} {...selectionProps} />;
  }
  return (
    <DiffParagraphGroupView
      group={element}
      itemIndex={itemIndex}
      activeParagraphId={selectionProps.activeParagraphId}
      activeSpanChangeId={selectionProps.activeSpanChangeId}
      onParagraphSelect={selectionProps.onParagraphSelect}
      onSpanSelect={selectionProps.onSpanSelect}
    />
  );
};

// ============================================
// DsdDsdCompareItem 렌더링 (아이템 단위 카드)
// ============================================

const CARD_SHADOW =
  "shadow-[0px_20px_25px_-5px_rgba(0,0,0,0.1),0px_4px_24px_4px_rgba(0,0,0,0.08)]";

const CARD_BASE =
  "bg-white border border-line-normal rounded-2xl overflow-clip px-3 py-5 [content-visibility:auto] [contain-intrinsic-size:auto_500px]";

const PLACEHOLDER_CONFIG = {
  deleted: {
    icon: CircleX,
    iconBg: "bg-red-500",
    lines: ["이전 버전에는 존재하나,", "현재 버전에는 없는 항목입니다."],
  },
  inserted: {
    icon: CirclePlus,
    iconBg: "bg-lime-500",
    lines: ["현재 버전에는 존재하나,", "이전 버전에는 없는 항목입니다."],
  },
} as const;

const EmptyDiffPlaceholder = ({ kind }: { kind: "deleted" | "inserted" }) => {
  const { icon: Icon, iconBg, lines } = PLACEHOLDER_CONFIG[kind];
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-2.5",
        "h-full min-h-[160px]",
        "rounded-[6px] border border-dashed border-line-normal",
        "bg-background-neutral-alternative",
      )}
    >
      <div className={cn("rounded-[6px] p-2", iconBg)}>
        <Icon className="size-5 text-static-white" />
      </div>
      <p className="label-2-medium text-label-normal text-center tracking-[0.25px]">
        {lines[0]}
        <br />
        {lines[1]}
      </p>
    </div>
  );
};

const DiffPanel = ({
  element,
  placeholderKind,
  mode,
  itemIndex,
  ...selectionProps
}: {
  element: DiffElement | null;
  placeholderKind?: "deleted" | "inserted";
  mode: DsdDsdViewMode;
  itemIndex: number;
} & CellSelectionProps) => (
  <div className="flex-1 min-w-0 overflow-x-auto">
    {element ? (
      <DiffElementView
        element={element}
        mode={mode}
        itemIndex={itemIndex}
        {...selectionProps}
      />
    ) : placeholderKind ? (
      <EmptyDiffPlaceholder kind={placeholderKind} />
    ) : null}
  </div>
);

// memo: 결과 뷰가 선택 상태를 아이템 단위로 슬라이스해 내려주므로(안 걸린 카드는
// null/EMPTY_SET 안정 참조), 클릭 시 선택이 걸린 한두 카드만 리렌더된다.
// 수만 셀 문서에서 클릭마다 전체 카드를 다시 그리던 병목의 해소 지점.
export const DiffItemView = memo(function DiffItemView({
  item,
  mode,
  itemIndex,
  ...selectionProps
}: {
  item: DsdDsdCompareItem;
  mode: DsdDsdViewMode;
  itemIndex: number;
} & CellSelectionProps) {
  const isMatchedParagraphPair =
    item.kind === "matched" &&
    item.srcElement &&
    item.trgElement &&
    isParagraphElement(item.srcElement) &&
    isParagraphElement(item.trgElement);

  return (
    <div className={cn(CARD_BASE, CARD_SHADOW)}>
      {isMatchedParagraphPair ? (
        <DiffParagraphPairGridView
          srcGroup={item.srcElement as DiffParagraphGroup}
          trgGroup={item.trgElement as DiffParagraphGroup}
          itemIndex={itemIndex}
          activeParagraphId={selectionProps.activeParagraphId}
          activeSpanChangeId={selectionProps.activeSpanChangeId}
          onParagraphSelect={selectionProps.onParagraphSelect}
          onSpanSelect={selectionProps.onSpanSelect}
        />
      ) : (
        <div className="flex gap-6">
          <DiffPanel
            element={item.srcElement}
            placeholderKind={item.kind === "inserted" ? "inserted" : undefined}
            mode={mode}
            itemIndex={itemIndex}
            {...selectionProps}
          />
          <DiffPanel
            element={item.trgElement}
            placeholderKind={item.kind === "deleted" ? "deleted" : undefined}
            mode={mode}
            itemIndex={itemIndex}
            {...selectionProps}
          />
        </div>
      )}
    </div>
  );
});

// ============================================
// DsdDsdCompareView (결과 뷰 전체)
// ============================================

const isChanged = (item: DsdDsdCompareItem) =>
  item.kind !== "matched" ||
  (item.diffCounts !== null && !item.diffCounts.isIdentical);

const hasSumCaution = (item: DsdDsdCompareItem) => {
  const elements = [item.srcElement, item.trgElement, item.element].filter(
    Boolean,
  );
  return elements.some(
    (el) =>
      el &&
      "rows" in el &&
      el.rows.some((row) => row.cells.some((cell) => cell.sumCaution)),
  );
};

const countChanges = (items: DsdDsdCompareItem[]) =>
  items.filter(isChanged).length;

const countSumDiffs = (items: DsdDsdCompareItem[]) =>
  items.filter(hasSumCaution).length;

/** 목차 점: 내용 변경·구조 diff와 합계 불일치 모두 표시 (필터의 변경+합계와 동일 기준) */
const sectionHasVisibleDiff = (item: DsdDsdCompareItem) =>
  isChanged(item) || hasSumCaution(item);

/** 사이드바에 띄울 섹션 이름. SRC 에만 있는 목차는 감춘다.
 *
 *  유저는 최신 보고서(TRG)에 관심이 있으므로, TRG 에 실체가 없는 목차는 사이드바에
 *  띄우지 않는다. 본문은 `result.sections` 전부를 그리므로 스크롤로는 도달한다. */
const sidebarSectionNames = (result: DsdDsdCompareResponse): string[] => {
  const hidden = new Set(result.srcOnlySections ?? []);
  return result.sections.filter((name) => !hidden.has(name));
};

const buildSidebarCategories = (
  result: DsdDsdCompareResponse,
): CompareSidebarCategory[] => {
  const toSection = (name: string): CompareSidebarSection => ({
    id: name,
    label: name,
    hasChanges: result.items.some(
      (item) => item.sectionName === name && sectionHasVisibleDiff(item),
    ),
  });

  const body: CompareSidebarSection[] = [];
  const notes: CompareSidebarSection[] = [];
  for (const name of sidebarSectionNames(result)) {
    if (/^\d/.test(name)) {
      notes.push(toSection(name));
    } else {
      body.push(toSection(name));
    }
  }

  const categories: CompareSidebarCategory[] = [];
  if (body.length > 0) categories.push({ label: "본문", sections: body });
  if (notes.length > 0) categories.push({ label: "주석", sections: notes });
  return categories;
};

const filterItems = (
  items: DsdDsdCompareItem[],
  mode: DsdDsdViewMode,
): DsdDsdCompareItem[] => {
  const wantChanges = isChangesActive(mode);
  const wantSum = isSumDiffsActive(mode);

  if (wantChanges && wantSum) {
    return items.filter((item) => isChanged(item) || hasSumCaution(item));
  }
  if (wantChanges) return items.filter(isChanged);
  if (wantSum) return items.filter(hasSumCaution);
  return items;
};

export interface DsdDsdCompareViewProps {
  result: DsdDsdCompareResponse;
}

export const DsdDsdCompareView = ({ result }: DsdDsdCompareViewProps) => {
  const [mode, setMode] = useState<DsdDsdViewMode>("default");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [selectedCellId, setSelectedCellId] = useState<string | null>(null);
  const [navigatorIndex, setNavigatorIndex] = useState<number | null>(null);
  const [headerHidden, setHeaderHidden] = useState(false);
  const lastScrollTop = useRef(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const downloadPendingRef = useRef(false);

  const sidebarCategories = useMemo(
    () => buildSidebarCategories(result),
    [result],
  );
  const changesCount = useMemo(() => countChanges(result.items), [result]);
  const sumDiffsCount = useMemo(() => countSumDiffs(result.items), [result]);
  const filteredItems = useMemo(
    () => filterItems(result.items, mode),
    [result, mode],
  );

  const cellMap = useMemo(() => buildCellMap(result), [result]);

  // 모든 matched 테이블에서 src↔trg cellId 양방향 매핑 (일반 셀 pair 선택용)
  const cellPairMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of result.items) {
      if (item.kind !== "matched") continue;
      const src = item.srcElement;
      const trg = item.trgElement;
      if (!src || !trg || !("rows" in src) || !("rows" in trg)) continue;
      const trgByAddr = new Map<string, string>();
      for (const row of trg.rows)
        for (const cell of row.cells)
          if (!cell.isGhost) trgByAddr.set(cell.address, cell.cellId);
      for (const row of src.rows)
        for (const cell of row.cells) {
          if (cell.isGhost) continue;
          const trgId = trgByAddr.get(cell.address);
          if (trgId) {
            map.set(cell.cellId, trgId);
            map.set(trgId, cell.cellId);
          }
        }
    }
    return map;
  }, [result]);

  // 변경사항 리스트 + cellId → index 역방향 맵
  const changeEntries = useMemo(
    () => buildChangeList(filteredItems, mode),
    [filteredItems, mode],
  );

  useEffect(() => {
    if (navigatorIndex != null && navigatorIndex >= changeEntries.length) {
      setNavigatorIndex(null);
      setSelectedCellId(null);
    }
  }, [navigatorIndex, changeEntries.length]);

  const cellIdToChangeIndex = useMemo(
    () => buildCellIdToChangeIndex(changeEntries),
    [changeEntries],
  );
  const paragraphIdToChangeIndex = useMemo(
    () => buildParagraphIdToChangeIndex(changeEntries),
    [changeEntries],
  );
  const spanIdToChangeIndex = useMemo(
    () => buildSpanIdToChangeIndex(changeEntries),
    [changeEntries],
  );

  // 합계/합계불일치 셀 선택 시 pair도 full selection 처리하기 위한 ID
  // changes 모드에서는 합계 정보가 숨겨지므로 비활성
  // navigatorIndex와 무관하게 동작 — 변경+합계 셀 클릭 시에도 pair 표시 필요
  const sumPairCellId = useMemo<string | null>(() => {
    if (!selectedCellId) return null;
    if (mode === "changes") return null;
    const cell = cellMap.get(selectedCellId);
    if (!cell) return null;
    if (cell.sumCaution || cell.sumRole === "parent") {
      return cellPairMap.get(selectedCellId) ?? null;
    }
    // 클릭한 셀은 일반이지만 pair가 합계/합계불일치인 경우
    const pairId = cellPairMap.get(selectedCellId);
    if (pairId) {
      const pairCell = cellMap.get(pairId);
      if (pairCell && (pairCell.sumCaution || pairCell.sumRole === "parent")) {
        return pairId;
      }
    }
    return null;
  }, [selectedCellId, mode, cellMap, cellPairMap]);

  const highlightedCellIds = useMemo<ReadonlySet<string>>(() => {
    if (mode === "changes") return EMPTY_SET;
    if (!selectedCellId) return EMPTY_SET;
    const cell = cellMap.get(selectedCellId);
    const ids: string[] = [];

    // 선택 셀 children
    if (cell?.sumChildren.length) {
      const i = selectedCellId.lastIndexOf("_");
      const prefix = i >= 0 ? selectedCellId.slice(0, i + 1) : "";
      ids.push(...cell.sumChildren.map((addr) => `${prefix}${addr}`));
    }

    // pair 셀 children (양쪽 하이라이트)
    if (sumPairCellId) {
      const pairCell = cellMap.get(sumPairCellId);
      if (pairCell?.sumChildren.length) {
        const j = sumPairCellId.lastIndexOf("_");
        const pairPrefix = j >= 0 ? sumPairCellId.slice(0, j + 1) : "";
        ids.push(...pairCell.sumChildren.map((addr) => `${pairPrefix}${addr}`));
      }
    }

    return ids.length > 0 ? new Set(ids) : EMPTY_SET;
  }, [mode, selectedCellId, cellMap, sumPairCellId]);

  // modified 쌍만 양쪽 셀 focused 표시, 그 외(삭제/추가/합계불일치)는 선택 셀만
  const focusedCellIds = useMemo<ReadonlySet<string>>(() => {
    if (navigatorIndex == null) return EMPTY_SET;
    const entry = changeEntries[navigatorIndex];
    if (!entry || entry.type !== "cell") return EMPTY_SET;
    const cell = entry.srcCell ?? entry.trgCell;
    if (cell?.status === "modified") return new Set(entry.cellIds);
    return EMPTY_SET;
  }, [navigatorIndex, changeEntries]);

  const activeParagraphId = useMemo(() => {
    if (navigatorIndex == null) return null;
    const entry = changeEntries[navigatorIndex];
    if (!entry || entry.type !== "paragraph") return null;
    return entry.paragraphId;
  }, [navigatorIndex, changeEntries]);

  const activeSpanChangeId = useMemo(() => {
    if (navigatorIndex == null) return null;
    const entry = changeEntries[navigatorIndex];
    if (!entry || entry.type !== "span") return null;
    return entry.spanChangeId;
  }, [navigatorIndex, changeEntries]);

  // 일반 셀 선택 시 자신 + pair 셀 ID (변경 셀 선택 시에는 비활성)
  const plainSelectedCellIds = useMemo<ReadonlySet<string>>(() => {
    if (!selectedCellId || navigatorIndex != null) return EMPTY_SET;
    const set = new Set([selectedCellId]);
    const pair = cellPairMap.get(selectedCellId);
    if (pair) set.add(pair);
    return set;
  }, [selectedCellId, navigatorIndex, cellPairMap]);

  // 셀 선택 시 세로 스크롤 + TableHorizontalNavigator 등 가로 overflow까지 맞춤
  useEffect(() => {
    const root = scrollContainerRef.current;
    if (!root || !selectedCellId) return;
    const ids = [
      selectedCellId,
      ...focusedCellIds,
      ...highlightedCellIds,
      ...plainSelectedCellIds,
    ];
    scrollCompareCellTargetsIntoView(root, ids, "smooth");
  }, [
    selectedCellId,
    focusedCellIds,
    highlightedCellIds,
    plainSelectedCellIds,
  ]);

  // 셀 클릭 → 선택 + 네비게이터 열기
  const handleCellSelect = useCallback(
    (cellId: string) => {
      setSelectedCellId((prev) => {
        if (prev === cellId) {
          setNavigatorIndex(null);
          return null;
        }
        const idx = cellIdToChangeIndex.get(cellId);
        setNavigatorIndex(idx ?? null);
        return cellId;
      });
    },
    [cellIdToChangeIndex],
  );

  // 문단 클릭 → 네비게이터 열기
  const handleParagraphSelect = useCallback(
    (paragraphId: string) => {
      const idx = paragraphIdToChangeIndex.get(paragraphId);
      if (idx != null) {
        setSelectedCellId(null);
        setNavigatorIndex((prev) => (prev === idx ? null : idx));
      }
    },
    [paragraphIdToChangeIndex],
  );

  // span 변경 클릭 → 네비게이터 열기
  const handleSpanSelect = useCallback(
    (spanId: string) => {
      const idx = spanIdToChangeIndex.get(spanId);
      if (idx != null) {
        setSelectedCellId(null);
        setNavigatorIndex((prev) => (prev === idx ? null : idx));
      }
    },
    [spanIdToChangeIndex],
  );

  // 네비게이터 이동 → 해당 셀 선택 + 스크롤
  const handleNavigate = useCallback(
    (index: number) => {
      const entry = changeEntries[index];
      if (!entry) return;

      setNavigatorIndex(index);

      if (entry.type === "cell") {
        const ids = entry.cellIds;
        if (ids.length > 0) {
          setSelectedCellId(ids[0]);
          // 스크롤은 selectedCellId / focused / highlight 변경 시 useEffect에서 처리
        }
      } else {
        setSelectedCellId(null);
        const changeId =
          entry.type === "span" ? entry.spanChangeId : entry.paragraphId;
        requestAnimationFrame(() => {
          const el = scrollContainerRef.current?.querySelector(
            `[data-change-id="${CSS.escape(changeId)}"]`,
          );
          el?.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest",
          });
        });
      }
    },
    [changeEntries],
  );

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const scrollTop = container.scrollTop;
    const delta = scrollTop - lastScrollTop.current;
    lastScrollTop.current = scrollTop;

    const atBottom =
      scrollTop + container.clientHeight >= container.scrollHeight - 1;

    if (scrollTop <= 0 || atBottom) {
      setHeaderHidden(false);
    } else if (delta > 4) {
      setHeaderHidden(true);
    } else if (delta < -4) {
      setHeaderHidden(false);
    }
  }, []);

  const handleNavigatorClose = useCallback(() => {
    setNavigatorIndex(null);
    setSelectedCellId(null);
  }, []);

  // 전체 모드 + 사이드바 열림 상태에서만 DOM 클론 → 다운로드 실행
  const executeDownload = useCallback(() => {
    if (!rootRef.current) return;
    // 선택 상태 해제를 동기적으로 DOM에 반영한 뒤 클론
    flushSync(() => {
      setSelectedCellId(null);
      setNavigatorIndex(null);
      setHeaderHidden(false);
    });
    downloadCompareHtml(rootRef.current, result, mode, filteredItems, {
      src: result.srcFilename,
      trg: result.trgFilename,
    });
  }, [result, mode, filteredItems]);

  // 모드/사이드바 변경 후 DOM 반영 완료 시 pending 다운로드 실행
  useEffect(() => {
    if (!downloadPendingRef.current) return;
    if (mode !== "default" || !sidebarOpen) return;
    downloadPendingRef.current = false;
    executeDownload();
  }, [mode, sidebarOpen, executeDownload]);

  const { mutate: logDownload } = useLogDsdDsdCompareDownload();

  const handleDownload = useCallback(() => {
    logDownload();
    if (mode === "default" && sidebarOpen) {
      executeDownload();
      return;
    }
    // 전체 모드 + 사이드바 열기 → re-render 후 effect에서 다운로드
    downloadPendingRef.current = true;
    setSelectedCellId(null);
    setNavigatorIndex(null);
    setHeaderHidden(false);
    setMode("default");
    setSidebarOpen(true);
  }, [mode, sidebarOpen, executeDownload, logDownload]);

  const itemsBySection = useMemo(() => {
    const map = new Map<
      string,
      { item: DsdDsdCompareItem; globalIdx: number }[]
    >();
    for (const [globalIdx, item] of filteredItems.entries()) {
      if (!map.has(item.sectionName)) map.set(item.sectionName, []);
      map.get(item.sectionName)!.push({ item, globalIdx });
    }
    return map;
  }, [filteredItems]);

  // mode에 따라 해당 아이템이 있는 섹션만 사이드바에 표시
  const visibleSidebarCategories = useMemo(() => {
    if (mode === "default") return sidebarCategories;
    return sidebarCategories
      .map((cat) => ({
        ...cat,
        sections: cat.sections.filter((s) => itemsBySection.has(s.id)),
      }))
      .filter((cat) => cat.sections.length > 0);
  }, [sidebarCategories, itemsBySection, mode]);

  // 스크롤 하이라이트가 따라갈 섹션 = 사이드바에 실제로 버튼이 있는 섹션.
  // 사이드바 카테고리에서 직접 뽑아야 두 목록이 어긋날 여지가 없다. 순서는
  // result.sections(문서 순서)로 되돌린다 — 관측 결과에서 "가장 위에 보이는 섹션"을
  // 고를 때 본문/주석 카테고리 분류 순서가 아니라 문서 순서여야 한다.
  const observedSectionIds = useMemo(() => {
    const hasButton = new Set<string>();
    for (const category of visibleSidebarCategories) {
      for (const section of category.sections) hasButton.add(section.id);
    }
    return result.sections.filter((name) => hasButton.has(name));
  }, [visibleSidebarCategories, result.sections]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const visibleSet = new Set<string>();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = entry.target.id.replace("section-", "");
          if (entry.isIntersecting) visibleSet.add(id);
          else visibleSet.delete(id);
        });
        const topVisible = observedSectionIds.find((s) => visibleSet.has(s));
        if (topVisible) setActiveSection(topVisible);
      },
      { root: container, rootMargin: "0px 0px -70% 0px", threshold: 0 },
    );

    // 버튼 없는 섹션(SRC 전용)을 관측하면 그 구간을 지날 때 activeSection 이
    // 버튼 없는 이름으로 바뀌어 하이라이트가 빈다.
    observedSectionIds.forEach((sectionId) => {
      const el = document.getElementById(`section-${sectionId}`);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [observedSectionIds]);

  const handleSectionClick = useCallback((section: CompareSidebarSection) => {
    const el = document.getElementById(`section-${section.id}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  // 표 셀 드래그 다중 선택 + Ctrl+C 복사 (ixd-dsd DSD 프리뷰와 동일 프리셋).
  // - 단일 클릭은 기존 액션(셀 선택→네비게이터)이 소유한다. handleCellSelect 는 토글
  //   시맨틱이라 단일 선택을 여기에도 배선하면 클릭 경로와 이중 호출로 상쇄된다 —
  //   다중(>=2)에만 반응하고, 단일 클릭 시각도 minSelectionToStyle=2 로 띄우지 않는다.
  // - ←/→ 는 변경 네비게이터 단축키이므로 공통 훅의 방향키 선택 이동은 끈다.
  // - 헤더(th)도 드래그 대상이고(DiffCellView 가 th 에도 선택 계약을 붙인다), 병합 셀이
  //   많은 DSD 표라 드래그 범위는 엑셀 그리드 시맨틱을 쓴다.
  const multiSelectActiveRef = useRef(false);
  useTableCellSelection(scrollContainerRef, {
    styleId: "dsd-dsd-compare-cell-selection-style",
    autoBindContainer: true,
    includeHeaderCells: true,
    enableGridRangeSelection: true,
    clipboardTableFormat: "excel-table",
    enableArrowNavigation: false,
    selectionCss: MULTI_SELECT_CELL_BORDER_CSS,
    minSelectionToStyle: 2,
    onSelectionChange: (uniqueIds) => {
      const isMulti = uniqueIds.length >= 2;
      multiSelectActiveRef.current = isMulti;
      // 다중 선택이 단일 선택 오버레이를 이긴다 — 두 강조가 겹쳐 보이지 않게.
      if (isMulti) setSelectedCellId(null);
    },
  });

  // ── 선택 상태의 아이템 단위 슬라이싱 ──
  // 이 문서는 셀이 수만 개라, 클릭 선택 상태를 전 아이템에 그대로 흘리면 카드 전부가
  // 리렌더돼 클릭 한 번에 수백 ms 가 걸린다. 선택은 항상 한두 아이템에만 걸리므로,
  // 각 상태를 "소속 아이템 것만 실값, 나머지는 안정 참조(null/EMPTY_SET)"로 쪼개
  // memo(DiffItemView) 가 무관한 카드의 리렌더를 건너뛰게 한다.

  // 셀 → 소속 아이템(globalIdx) 역방향 맵.
  // 카드는 filteredItems 의 인덱스(globalIdx)로 키를 받으므로 같은 배열로 만든다 —
  // result.items 로 만들면 변경사항·합계불일치 모드처럼 항목이 걸러질 때 두 인덱스
  // 공간이 어긋나 선택 상태가 엉뚱한(또는 없는) 카드로 가고 클릭한 셀에 링이 안 뜬다.
  const cellIdToItemIndex = useMemo(() => {
    const map = new Map<string, number>();
    filteredItems.forEach((item, idx) => {
      for (const element of [item.srcElement, item.trgElement, item.element]) {
        if (element && "rows" in element) {
          for (const row of element.rows) {
            for (const cell of row.cells) map.set(cell.cellId, idx);
          }
        }
      }
    });
    return map;
  }, [filteredItems]);

  const sliceByItem = useCallback(
    (ids: ReadonlySet<string>): Map<number, ReadonlySet<string>> => {
      const map = new Map<number, Set<string>>();
      for (const id of ids) {
        const idx = cellIdToItemIndex.get(id);
        if (idx === undefined) continue;
        let set = map.get(idx);
        if (!set) {
          set = new Set<string>();
          map.set(idx, set);
        }
        set.add(id);
      }
      return map;
    },
    [cellIdToItemIndex],
  );

  const focusedByItem = useMemo(
    () => sliceByItem(focusedCellIds),
    [sliceByItem, focusedCellIds],
  );
  const highlightedByItem = useMemo(
    () => sliceByItem(highlightedCellIds),
    [sliceByItem, highlightedCellIds],
  );
  const plainSelectedByItem = useMemo(
    () => sliceByItem(plainSelectedCellIds),
    [sliceByItem, plainSelectedCellIds],
  );

  const selectedItemIndex =
    selectedCellId != null
      ? (cellIdToItemIndex.get(selectedCellId) ?? null)
      : null;
  const sumPairItemIndex =
    sumPairCellId != null
      ? (cellIdToItemIndex.get(sumPairCellId) ?? null)
      : null;
  const activeParagraphItemIndex = itemIndexFromChangeId(activeParagraphId);
  const activeSpanItemIndex = itemIndexFromChangeId(activeSpanChangeId);

  const getCellDomId = useCallback(
    (cell: DiffCell) => compareCellDomId(cell.cellId),
    [],
  );

  // 사이드바 토글 시 IntersectionObserver → setActiveSection → re-render 발생.
  // 무거운 콘텐츠 JSX를 메모이제이션하여 불필요한 재생성 방지.
  // 선택 변경 시 이 JSX 서술자 재생성(아이템 수백 개)은 허용한다 — 실제 렌더는
  // memo(DiffItemView) 가 아이템 단위로 걸러, 선택이 걸린 카드만 다시 그린다.
  const scrollContent = useMemo(
    () => (
      <div className="flex flex-col gap-5 px-5 pb-5 pt-14">
        {result.sections.map((sectionName) => {
          const entries = itemsBySection.get(sectionName);
          if (!entries?.length) return null;
          return (
            <div
              key={sectionName}
              id={`section-${sectionName}`}
              className="flex flex-col gap-3"
            >
              {entries.map(({ item, globalIdx }) => (
                <DiffItemView
                  key={globalIdx}
                  item={item}
                  itemIndex={globalIdx}
                  mode={mode}
                  selectedCellId={
                    selectedItemIndex === globalIdx ? selectedCellId : null
                  }
                  sumPairCellId={
                    sumPairItemIndex === globalIdx ? sumPairCellId : null
                  }
                  focusedCellIds={focusedByItem.get(globalIdx) ?? EMPTY_SET}
                  highlightedCellIds={
                    highlightedByItem.get(globalIdx) ?? EMPTY_SET
                  }
                  plainSelectedCellIds={
                    plainSelectedByItem.get(globalIdx) ?? EMPTY_SET
                  }
                  activeParagraphId={
                    activeParagraphItemIndex === globalIdx
                      ? activeParagraphId
                      : null
                  }
                  activeSpanChangeId={
                    activeSpanItemIndex === globalIdx ? activeSpanChangeId : null
                  }
                  onCellSelect={handleCellSelect}
                  getCellDomId={getCellDomId}
                  onParagraphSelect={handleParagraphSelect}
                  onSpanSelect={handleSpanSelect}
                />
              ))}
            </div>
          );
        })}

        {filteredItems.length === 0 && (
          <div className="flex items-center justify-center py-20">
            <p className="body-1-normal-medium text-label-assistive">
              해당하는 항목이 없습니다.
            </p>
          </div>
        )}
      </div>
    ),
    [
      result.sections,
      itemsBySection,
      filteredItems.length,
      mode,
      selectedCellId,
      selectedItemIndex,
      sumPairCellId,
      sumPairItemIndex,
      focusedByItem,
      highlightedByItem,
      plainSelectedByItem,
      activeParagraphId,
      activeParagraphItemIndex,
      activeSpanChangeId,
      activeSpanItemIndex,
      handleCellSelect,
      getCellDomId,
      handleParagraphSelect,
      handleSpanSelect,
    ],
  );

  return (
    <SidebarProvider
      ref={rootRef}
      open={sidebarOpen}
      onOpenChange={setSidebarOpen}
      className="h-screen"
      style={
        {
          "--sidebar-width": "192px",
          "--sidebar-width-icon": "3rem",
          minHeight: 0,
        } as React.CSSProperties
      }
    >
      <CompareSidebar
        periodInfo={result.period}
        categories={visibleSidebarCategories}
        activeSection={activeSection}
        onSectionClick={handleSectionClick}
      />

      <SidebarInset className="flex flex-col min-w-0">
        <CompareHeader
          mode={mode}
          onModeChange={setMode}
          sumDiffsCount={sumDiffsCount}
          changesCount={changesCount}
          srcFilename={result.srcFilename}
          trgFilename={result.trgFilename}
          onDownload={handleDownload}
          hidden={headerHidden}
        />

        <div
          ref={scrollContainerRef}
          className="flex-1 overflow-y-auto bg-background-neutral-alternative pb-80"
          onScroll={handleScroll}
          onClick={() => {
            // 드래그 다중 선택 종료 시 click 은 (mousedown/mouseup 타깃이 달라)
            // 공통 조상인 여기서 발화한다 — 빈 영역 클릭으로 오인해 네비게이터를
            // 닫지 않는다.
            if (multiSelectActiveRef.current) return;
            setSelectedCellId(null);
            setNavigatorIndex(null);
          }}
        >
          {scrollContent}
        </div>
      </SidebarInset>

      {navigatorIndex != null && (
        <ChangeNavigatorPanel
          entries={changeEntries}
          currentIndex={navigatorIndex}
          activeCellId={selectedCellId}
          mode={mode}
          onNavigate={handleNavigate}
          onClose={handleNavigatorClose}
        />
      )}
    </SidebarProvider>
  );
};

// ============================================
// DsdDsdCompareResultPage (라우트 엔트리)
// ============================================

export const DsdDsdCompareResultPage = () => {
  const [searchParams] = useSearchParams();
  const srcParam = searchParams.get("src");
  const trgParam = searchParams.get("trg");

  const {
    mutate,
    data: result,
    isPending,
  } = useMutation({
    mutationFn: ({ src, trg }: { src: string; trg: string }) =>
      compareDsdDsd(src, trg),
  });

  useEffect(() => {
    if (!srcParam || !trgParam) return;
    mutate({ src: srcParam, trg: trgParam });
  }, [srcParam, trgParam, mutate]);

  if (isPending && !result) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="size-8 animate-spin text-label-assistive" />
          <p className="body-1-normal-medium text-label-assistive">
            DSD 대사 진행 중...
          </p>
        </div>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-white">
        <p className="body-1-normal-medium text-label-assistive">
          비교할 파일 정보가 없습니다.
        </p>
      </div>
    );
  }

  return <DsdDsdCompareView result={result} />;
};
